// DNA CONNECT - Comunicação (Visual Premium)
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  MessageCircle, Bell, Paperclip, CheckCircle, Circle,
  Loader2, Clock, AlertTriangle, Info, Sparkles, 
  Download, ExternalLink, ChevronRight, X
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

const msgImages = (m) => {
  if (m?.images?.length) return m.images.filter(i => i?.url);
  if (m?.image_url) return [{ url: m.image_url, filename: m.image_filename }];
  return [];
};

export default function DnaConnectComunicacao() {
  const [loading, setLoading] = useState(true);
  const [mensagens, setMensagens] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [filter, setFilter] = useState('all'); // all, unread, urgent
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    loadMensagens();
  }, []);

  const loadMensagens = async () => {
    try {
      const token = localStorage.getItem('familyToken');
      const response = await fetch(`${API_URL}/api/dnaconnect/comunicacao/mensagens?token=${token}`);
      const data = await response.json();
      setMensagens(data);
    } catch (error) {
      toast.error('Erro ao carregar mensagens');
    } finally {
      setLoading(false);
    }
  };

  const marcarComoLida = async (messageId) => {
    try {
      const token = localStorage.getItem('familyToken');
      await fetch(`${API_URL}/api/dnaconnect/comunicacao/mensagens/${messageId}/read?token=${token}`, {
        method: 'POST'
      });
      
      setMensagens(prev => prev.map(m => 
        m.id === messageId ? { ...m, read: true } : m
      ));
    } catch (error) {
      console.error('Erro ao marcar como lida:', error);
    }
  };

  const openMessage = (message) => {
    setSelectedMessage(message);
    if (!message.read) {
      marcarComoLida(message.id);
    }
  };

  const getTypeConfig = (type) => {
    switch (type) {
      case 'urgente':
        return {
          icon: AlertTriangle,
          gradient: 'from-red-500 to-rose-600',
          bgLight: 'bg-red-50',
          border: 'border-red-200',
          text: 'text-red-700',
          badge: 'bg-red-500 text-white',
          glow: 'shadow-red-200'
        };
      case 'informativo':
        return {
          icon: Info,
          gradient: 'from-blue-500 to-cyan-600',
          bgLight: 'bg-blue-50',
          border: 'border-blue-200',
          text: 'text-blue-700',
          badge: 'bg-blue-500 text-white',
          glow: 'shadow-blue-200'
        };
      default:
        return {
          icon: Bell,
          gradient: 'from-purple-500 to-violet-600',
          bgLight: 'bg-purple-50',
          border: 'border-purple-200',
          text: 'text-purple-700',
          badge: 'bg-purple-500 text-white',
          glow: 'shadow-purple-200'
        };
    }
  };

  const filteredMessages = mensagens.filter(m => {
    if (filter === 'unread') return !m.read;
    if (filter === 'urgent') return m.type === 'urgente';
    return true;
  });

  const unreadCount = mensagens.filter(m => !m.read).length;
  const urgentCount = mensagens.filter(m => m.type === 'urgente' && !m.read).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-purple-600 mx-auto mb-3" />
          <p className="text-gray-500">Carregando mensagens...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Premium */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-purple-600 via-violet-600 to-purple-700 p-6 text-white">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="absolute -right-10 -top-10 w-40 h-40 bg-white/10 rounded-full blur-2xl"></div>
        <div className="absolute -left-10 -bottom-10 w-32 h-32 bg-white/5 rounded-full blur-xl"></div>
        
        <div className="relative">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
              <MessageCircle className="w-6 h-6" />
            </div>
            <h1 className="text-2xl font-bold">Comunicação</h1>
          </div>
          <p className="text-purple-100 text-sm">
            Fique por dentro de todas as novidades da escola
          </p>
          
          {/* Stats */}
          <div className="flex gap-4 mt-4">
            <div className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-2">
              <p className="text-2xl font-bold">{mensagens.length}</p>
              <p className="text-xs text-purple-200">Total</p>
            </div>
            {unreadCount > 0 && (
              <div className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-2">
                <p className="text-2xl font-bold">{unreadCount}</p>
                <p className="text-xs text-purple-200">Não lidas</p>
              </div>
            )}
            {urgentCount > 0 && (
              <div className="bg-red-500/80 backdrop-blur-sm rounded-xl px-4 py-2 animate-pulse">
                <p className="text-2xl font-bold">{urgentCount}</p>
                <p className="text-xs text-red-100">Urgentes</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        <Button
          variant={filter === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('all')}
          className={filter === 'all' ? 'bg-purple-600 hover:bg-purple-700' : ''}
        >
          Todas
        </Button>
        <Button
          variant={filter === 'unread' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('unread')}
          className={filter === 'unread' ? 'bg-purple-600 hover:bg-purple-700' : ''}
        >
          Não lidas ({unreadCount})
        </Button>
        <Button
          variant={filter === 'urgent' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('urgent')}
          className={filter === 'urgent' ? 'bg-red-600 hover:bg-red-700' : ''}
        >
          <AlertTriangle className="w-4 h-4 mr-1" />
          Urgentes
        </Button>
      </div>

      {/* Lista de Mensagens - Cards Premium */}
      {filteredMessages.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="text-center py-16">
            <div className="w-20 h-20 mx-auto mb-4 bg-purple-100 rounded-full flex items-center justify-center">
              <MessageCircle className="w-10 h-10 text-purple-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-700 mb-1">
              {filter === 'all' ? 'Nenhuma mensagem' : 'Nenhuma mensagem encontrada'}
            </h3>
            <p className="text-gray-500 text-sm">
              {filter === 'unread' 
                ? 'Você está em dia com todas as mensagens!' 
                : 'As mensagens da escola aparecerão aqui'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredMessages.map((mensagem) => {
            const config = getTypeConfig(mensagem.type);
            const Icon = config.icon;
            
            return (
              <Card
                key={mensagem.id}
                onClick={() => openMessage(mensagem)}
                className={`
                  cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:shadow-xl
                  ${!mensagem.read ? `${config.bgLight} ${config.border} border-2 ${config.glow} shadow-lg` : 'border hover:border-purple-200'}
                  overflow-hidden group
                `}
              >
                <CardContent className="p-0">
                  <div className="flex">
                    {/* Indicator Bar */}
                    <div className={`w-1.5 bg-gradient-to-b ${config.gradient}`}></div>
                    
                    <div className="flex-1 p-4">
                      <div className="flex items-start gap-3">
                        {/* Status Icon */}
                        <div className={`
                          flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center
                          ${!mensagem.read 
                            ? `bg-gradient-to-br ${config.gradient} text-white shadow-lg` 
                            : 'bg-gray-100 text-gray-400'
                          }
                          transition-transform group-hover:scale-110
                        `}>
                          {!mensagem.read ? (
                            <Icon className="w-6 h-6" />
                          ) : (
                            <CheckCircle className="w-6 h-6" />
                          )}
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <Badge className={`${config.badge} text-xs`}>
                              {mensagem.type === 'urgente' ? 'URGENTE' : 
                               mensagem.type === 'informativo' ? 'INFO' : 'AVISO'}
                            </Badge>
                            {!mensagem.read && (
                              <Badge className="bg-purple-600 text-white text-xs animate-pulse">
                                <Sparkles className="w-3 h-3 mr-1" />
                                Nova
                              </Badge>
                            )}
                            {mensagem.attachments?.length > 0 && (
                              <Badge variant="outline" className="text-xs gap-1">
                                <Paperclip className="w-3 h-3" />
                                {mensagem.attachments.length}
                              </Badge>
                            )}
                          </div>
                          
                          <h3 className={`font-semibold text-base mb-1 line-clamp-1 ${!mensagem.read ? config.text : 'text-gray-800'}`}>
                            {mensagem.title}
                          </h3>
                          
                          <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                            {mensagem.content}
                          </p>

                          {msgImages(mensagem).length > 0 && (
                            <div className={`mb-2 grid gap-1.5 ${msgImages(mensagem).length > 1 ? 'grid-cols-3' : 'grid-cols-1'}`}>
                              {msgImages(mensagem).slice(0, 3).map((img, i) => (
                                <div key={`${img.url}-${i}`} className="relative">
                                  <img
                                    src={img.url}
                                    alt={img.filename || 'imagem do comunicado'}
                                    className={`rounded-lg w-full object-cover border ${msgImages(mensagem).length > 1 ? 'h-20' : 'max-h-40'}`}
                                    loading="lazy"
                                    data-testid={`msg-card-image-${mensagem.id}-${i}`}
                                  />
                                  {i === 2 && msgImages(mensagem).length > 3 && (
                                    <span className="absolute inset-0 rounded-lg bg-black/50 text-white text-xs font-semibold flex items-center justify-center">
                                      +{msgImages(mensagem).length - 3}
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                          
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-400 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {new Date(mensagem.created_at).toLocaleDateString('pt-BR', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric'
                              })}
                            </span>
                            
                            <span className={`text-xs font-medium flex items-center gap-1 ${config.text} opacity-0 group-hover:opacity-100 transition-opacity`}>
                              Ler mais
                              <ChevronRight className="w-4 h-4" />
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Dialog de Mensagem - Visual Premium */}
      <Dialog open={!!selectedMessage} onOpenChange={() => setSelectedMessage(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col p-0">
          {selectedMessage && (() => {
            const config = getTypeConfig(selectedMessage.type);
            const Icon = config.icon;
            
            return (
              <>
                {/* Header Colorido */}
                <div className={`bg-gradient-to-r ${config.gradient} p-6 text-white relative`}>
                  <button 
                    onClick={() => setSelectedMessage(null)}
                    className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                  
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                      <Icon className="w-6 h-6" />
                    </div>
                    <Badge className="bg-white/30 text-white border-0">
                      {selectedMessage.type === 'urgente' ? 'URGENTE' : 
                       selectedMessage.type === 'informativo' ? 'INFORMATIVO' : 'AVISO'}
                    </Badge>
                  </div>
                  
                  <h2 className="text-xl font-bold leading-tight">
                    {selectedMessage.title}
                  </h2>
                  
                  <p className="text-sm text-white/70 mt-2 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    {new Date(selectedMessage.created_at).toLocaleString('pt-BR', {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
                
                {/* Conteúdo */}
                <div className="flex-1 overflow-y-auto p-6">
                  <div className="prose prose-sm max-w-none">
                    <p className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                      {selectedMessage.content}
                    </p>
                  </div>

                  {msgImages(selectedMessage).length > 0 && (
                    <div
                      className={`mt-4 grid gap-2 ${msgImages(selectedMessage).length === 1
                        ? 'grid-cols-1'
                        : 'grid-cols-2 sm:grid-cols-3'}`}
                      data-testid="msg-dialog-gallery"
                    >
                      {msgImages(selectedMessage).map((img, i) => (
                        <button
                          key={`${img.url}-${i}`}
                          type="button"
                          onClick={() => setLightbox(img.url)}
                          className="group relative overflow-hidden rounded-xl border shadow-sm"
                          data-testid={`msg-dialog-image-${i}`}
                          aria-label={`Ampliar imagem ${i + 1}`}
                        >
                          <img
                            src={img.url}
                            alt={img.filename || `imagem ${i + 1} do comunicado`}
                            loading="lazy"
                            className={`w-full object-cover cursor-zoom-in transition group-hover:opacity-95 ${
                              msgImages(selectedMessage).length === 1 ? 'max-h-[420px]' : 'h-32 sm:h-36'}`}
                          />
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {/* Anexos */}
                  {selectedMessage.attachments?.length > 0 && (
                    <div className="mt-6 pt-6 border-t">
                      <h4 className="font-semibold mb-3 flex items-center gap-2 text-gray-800">
                        <Paperclip className="w-5 h-5 text-purple-600" />
                        Anexos ({selectedMessage.attachments.length})
                      </h4>
                      <div className="grid gap-2">
                        {selectedMessage.attachments.map((anexo, idx) => (
                          <a
                            key={idx}
                            href={anexo.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-purple-50 hover:border-purple-200 border border-transparent transition-all group"
                          >
                            <div className="p-2 bg-purple-100 rounded-lg group-hover:bg-purple-200 transition-colors">
                              <Download className="w-5 h-5 text-purple-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-800 truncate">
                                {anexo.name || `Anexo ${idx + 1}`}
                              </p>
                              <p className="text-xs text-gray-500">Clique para baixar</p>
                            </div>
                            <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-purple-600" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Footer */}
                <div className="p-4 border-t bg-gray-50">
                  <Button 
                    onClick={() => setSelectedMessage(null)}
                    className={`w-full bg-gradient-to-r ${config.gradient} hover:opacity-90`}
                  >
                    Fechar
                  </Button>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Lightbox de imagem */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-3"
          onClick={() => setLightbox(null)}
          data-testid="msg-image-lightbox"
        >
          <img src={lightbox} alt="Imagem do comunicado" className="max-w-full max-h-full rounded-lg shadow-2xl" />
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-3 right-3 bg-white/20 text-white p-2 rounded-full hover:bg-white/30"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  );
}
